import React from 'react';
import { Check, Zap, Leaf, TrendingUp, Heart } from 'lucide-react';
import { PARTNERSHIP_PAGE } from '../content';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const Partnership = () => {
  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-pink-50 via-white to-green-50 py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-pink-100 to-pink-200 rounded-full mb-6">
              <Heart className="text-pink-600" size={20} />
              <span className="text-pink-700 font-semibold text-sm">{PARTNERSHIP_PAGE.hero.badge}</span>
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-8">
              {PARTNERSHIP_PAGE.hero.title} <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-pink-600">{PARTNERSHIP_PAGE.hero.titleHighlight}</span>
            </h1>
            
            {/* Logos Partnership */}
            <div className="flex items-center justify-center gap-8 mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_rt-energy/artifacts/cw77ddqz_RT.svg" 
                alt="RT Distribution" 
                className="h-20 w-auto"
              />
              <div className="text-4xl text-gray-400 font-light">×</div>
              <img 
                src="https://customer-assets.emergentagent.com/job_rt-energy/artifacts/kgcg3jhj_octopus%20energy.png" 
                alt="Octopus Energy France" 
                className="h-20 w-auto"
              />
            </div>
            
            <p className="text-xl text-gray-600">
              {PARTNERSHIP_PAGE.hero.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Partnership Story */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">{PARTNERSHIP_PAGE.story.title}</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                {PARTNERSHIP_PAGE.story.paragraphs.map((paragraph, index) => (
                  <p key={index} className={index === 2 ? "font-semibold text-gray-900" : ""}>
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/414837/pexels-photo-414837.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Renewable energy"
                  className="w-full h-[400px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg flex items-center justify-center">
                    <Zap className="text-white" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Depuis</p>
                    <p className="text-2xl font-bold text-gray-900">2023</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Octopus Energy Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Pourquoi Octopus Energy ?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Un fournisseur d'énergie révolutionnaire au service de la transition énergétique
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-6">
                <Leaf className="text-white" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Énergie 100% verte</h3>
              <p className="text-gray-600">
                Électricité 100% renouvelable et décarbonée, produite en France.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl flex items-center justify-center mb-6">
                <TrendingUp className="text-white" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Tarifs compétitifs</h3>
              <p className="text-gray-600">
                Offres jusqu'à 5% moins chères que les tarifs réglementés EDF.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center mb-6">
                <Zap className="text-white" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Innovation technologique</h3>
              <p className="text-gray-600">
                Service client digital et outils de suivi de consommation avancés.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mb-6">
                <Heart className="text-white" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Engagement social</h3>
              <p className="text-gray-600">
                Système de cagnotte reversable à des associations partenaires.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/433308/pexels-photo-433308.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Wind turbines"
                  className="w-full h-[500px] object-cover"
                />
              </div>
            </div>
            <div className="space-y-8 order-1 lg:order-2">
              <h2 className="text-4xl font-bold text-gray-900">Ce que nous proposons</h2>
              <div className="space-y-4">
                {PARTNERSHIP_PAGE.offer.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                      <Check className="text-white" size={18} />
                    </div>
                    <p className="text-lg text-gray-700 pt-0.5">{benefit}</p>
                  </div>
                ))}
              </div>
              <div className="pt-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white">
                    Nous contacter
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-pink-500 via-pink-600 to-pink-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-green-300 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              {PARTNERSHIP_PAGE.cta.title}
            </h2>
            <p className="text-xl text-pink-50 mb-8">
              {PARTNERSHIP_PAGE.cta.description}
            </p>
            <Link to="/careers">
              <Button size="lg" className="bg-white text-pink-600 hover:bg-gray-100 shadow-lg">
                {PARTNERSHIP_PAGE.cta.buttonText}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Partnership;